import { type ReactNode, createContext, useContext, useMemo } from "react";

interface CuratedFieldsValue {
  curated: ReadonlySet<string>;
  prefix: string;
}

const NONE: CuratedFieldsValue = {
  curated: new Set(),
  prefix: "",
};

const CuratedFieldsContext = createContext<CuratedFieldsValue>(NONE);

/**
 * Per-field artist curation markers (backend `curatedFields`). A field a
 * curator edited is kept by automatic writes (Tina asks before overwriting
 * it — see `docs/superpowers/specs/2026-09-28-artist-field-locks-design.md`);
 * this context lets any AutoForm wrapper show that as an info-only marker.
 * There is no unlock action any more — the badge is purely informational.
 * `prefix` maps a form's own keys onto curated names — the enrichment form's
 * `faq` is the curated name `enrichment.faq`.
 */
export function CuratedFieldsProvider({
  curated,
  prefix = "",
  children,
}: {
  curated: readonly string[] | null | undefined;
  prefix?: string;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ curated: new Set(curated ?? []), prefix }),
    [curated, prefix],
  );
  return (
    <CuratedFieldsContext.Provider value={value}>
      {children}
    </CuratedFieldsContext.Provider>
  );
}

/** `key` is the form's own field path; only a top-level key (no dot) can be curated. */
export function useFieldCuration(key: string) {
  const { curated, prefix } = useContext(CuratedFieldsContext);
  const name = `${prefix}${key}`;
  const isCurated = !key.includes(".") && curated.has(name);
  return { curated: isCurated };
}
